/**
 * Replay.tsx — /app/replay  (v8.3)
 *
 * Replay Intelligence workspace.
 * Consumes ThesisHistoryEntry[] → deterministic event/turning-point/summary analysis.
 *
 * Sub-tabs:
 *   TIMELINE     — ordered event stream with severity badges
 *   TURNING PTS  — high-significance moments with narratives
 *   SUMMARY      — session-level intelligence digest + lessons
 *   SIGNAL EVO   — raw thesis history (conviction + actionability over time)
 */
import React, { useState, useMemo } from 'react';
import { useStore }           from '../../store';
import { WatchlistSidebar }   from '../../features/watchlist/WatchlistSidebar';
import { getThesis, ThesisHistoryEntry } from '../../ai/thesisEngine';
import {
  analyseSnapshots,
  fromResearchSnapshots,
  ReplayIntelligenceOutput,
  ReplayEvent,
  ReplayIntelligenceSnapshot,
  REPLAY_INTELLIGENCE_THRESHOLDS as T,
} from '../../domains/replay/replayIntelligence';
import { loadSnapshotsForSymbol } from '../../research/researchSnapshot';
import {
  evaluateSession,
  type OutcomeValidationResult,
  type SessionAttributionSummary,
} from '../../domains/replay/outcomeValidation';
import {
  buildSessionIntelligenceSummary,
  type SessionIntelligenceSummary,
} from '../../domains/operator/operatorIntelligence';

// ── Design tokens ─────────────────────────────────────────────────────────────

const C = {
  gold:   '#C9A84C',
  border: '#1A3050',
  navy:   '#080F1C',
  card:   'rgba(13,31,53,0.55)',
  text:   '#E8EDF5',
  dim:    '#4A6FA5',
  muted:  '#3A5070',
  green:  '#4ADE80',
  red:    '#F87171',
  amber:  '#FCD34D',
  mono:   "'Courier New', monospace",
};

// ── Sub-component: severity badge ─────────────────────────────────────────────

function SevBadge({ sev }: { sev: string }) {
  const color = sev === 'high' ? C.red : sev === 'medium' ? C.amber : C.dim;
  return (
    <span style={{
      fontSize: 8, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase',
      color, border: `1px solid ${color}40`, borderRadius: 2, padding: '1px 5px',
      flexShrink: 0,
    }}>
      {sev}
    </span>
  );
}

// ── Sub-component: bias chip ──────────────────────────────────────────────────

function BiasBadge({ bias }: { bias: string }) {
  const color = bias === 'bullish' ? C.green : bias === 'bearish' ? C.red : C.dim;
  return (
    <span style={{ color, fontWeight: 700, textTransform: 'uppercase', fontSize: 10 }}>
      {bias}
    </span>
  );
}

// ── Sub-component: event type icon ────────────────────────────────────────────

function eventIcon(type: ReplayEvent['type']): string {
  switch (type) {
    case 'bias_flip':           return '⟳';
    case 'confidence_drop':     return '↘';
    case 'confidence_surge':    return '↗';
    case 'actionability_surge': return '⚡';
    case 'actionability_drop':  return '⬇';
    case 'market_state_shift': return '◈';
    case 'trap_triggered':        return '⚠';
    case 'breakout_text':      return '▶';
    default:                    return '·';
  }
}

// ── Tab: Timeline ─────────────────────────────────────────────────────────────

function TimelineTab({ intel }: { intel: ReplayIntelligenceOutput }) {
  if (intel.events.length === 0) return (
    <div style={{ color: C.muted, fontSize: 11, paddingTop: 40, textAlign: 'center' }}>
      No significant events detected in thesis history.
      {intel.summary === null && (
        <div style={{ marginTop: 8, fontSize: 10 }}>Add more analyses to populate the timeline.</div>
      )}
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ fontSize: 9, letterSpacing: 2, color: C.dim, marginBottom: 8 }}>
        {intel.events.length} EVENTS DETECTED — {intel.symbol}
      </div>
      {intel.events.map((e, i) => (
        <div key={i} style={{
          display: 'flex', gap: 10, alignItems: 'flex-start',
          padding: '8px 12px', background: C.card,
          border: `1px solid ${C.border}`, borderRadius: 3,
        }}>
          <span style={{ fontSize: 14, flexShrink: 0, width: 18, textAlign: 'center' }}>
            {eventIcon(e.type)}
          </span>
          <span style={{ color: C.dim, fontSize: 10, flexShrink: 0, minWidth: 55 }}>
            {new Date(e.ts).toLocaleTimeString()}
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <span style={{ color: C.text, fontSize: 11 }}>{e.reason}</span>
            <div style={{ fontSize: 9, color: C.muted, marginTop: 2, fontFamily: C.mono }}>
              {e.rule}
            </div>
          </div>
          <SevBadge sev={e.severity} />
        </div>
      ))}
    </div>
  );
}

// ── Tab: Turning Points ────────────────────────────────────────────────────────

function TurningPointsTab({ intel }: { intel: ReplayIntelligenceOutput }) {
  if (intel.turningPoints.length === 0) return (
    <div style={{ color: C.muted, fontSize: 11, paddingTop: 40, textAlign: 'center' }}>
      No major turning points detected.
      <div style={{ marginTop: 6, fontSize: 10 }}>
        Turning points require ≥{T.TURNING_POINT_MAJOR} simultaneous events at the same thesis entry.
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ fontSize: 9, letterSpacing: 2, color: C.dim, marginBottom: 4 }}>
        {intel.turningPoints.length} TURNING POINT{intel.turningPoints.length > 1 ? 'S' : ''} — {intel.symbol}
      </div>
      {intel.turningPoints.map((tp, i) => {
        const severityColor = tp.severity === 'critical' ? C.red : C.amber;
        return (
          <div key={i} style={{
            padding: '14px 16px', background: C.card,
            border: `1px solid ${severityColor}40`,
            borderLeft: `3px solid ${severityColor}`,
            borderRadius: 3,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, gap: 10 }}>
              <span style={{ color: C.dim, fontSize: 10 }}>
                {new Date(tp.ts).toLocaleTimeString()}
              </span>
              <span style={{
                fontSize: 8, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase',
                color: severityColor, border: `1px solid ${severityColor}50`,
                borderRadius: 2, padding: '1px 6px',
              }}>
                {tp.severity}
              </span>
            </div>
            <div style={{ color: C.text, fontSize: 11, lineHeight: 1.6, marginBottom: 10 }}>
              {tp.narrative}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {tp.events.map((e, j) => (
                <div key={j} style={{ display: 'flex', gap: 8, fontSize: 10, color: C.muted }}>
                  <span style={{ flexShrink: 0 }}>{eventIcon(e.type)}</span>
                  <span>{e.reason}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Tab: Session Summary ───────────────────────────────────────────────────────

function SummaryTab({ intel }: { intel: ReplayIntelligenceOutput }) {
  const s = intel.summary;
  if (!s) return (
    <div style={{ color: C.muted, fontSize: 11, paddingTop: 40, textAlign: 'center' }}>
      Need at least {T.MIN_ENTRIES_FOR_SUMMARY} thesis entries to generate a session summary.
    </div>
  );

  const trendColor = s.convictionTrend === 'rising' ? C.green
    : s.convictionTrend === 'falling' ? C.red : C.dim;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 720 }}>
      {/* Header */}
      <div style={{ padding: '16px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 3 }}>
        <div style={{ fontSize: 10, color: C.dim, letterSpacing: 2, marginBottom: 6 }}>SESSION LABEL</div>
        <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{s.sessionLabel}</div>
        <div style={{ marginTop: 8, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 10, color: C.muted }}>
            Dominant: <BiasBadge bias={s.dominantBias} />
          </span>
          <span style={{ fontSize: 10, color: C.muted }}>
            Trend: <span style={{ color: trendColor, fontWeight: 700 }}>{s.convictionTrend.toUpperCase()}</span>
          </span>
          <span style={{ fontSize: 10, color: C.muted }}>Entries: {s.entryCount}</span>
          <span style={{ fontSize: 10, color: C.muted }}>Events: {s.eventCount}</span>
        </div>
      </div>

      {/* Conviction stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
        {[
          { label: 'AVG CONVICTION', value: `${s.avgConviction}%`, color: C.gold },
          { label: 'PEAK',           value: `${s.peakConviction}%`, color: C.green },
          { label: 'TROUGH',         value: `${s.troughConviction}%`, color: C.red },
        ].map(m => (
          <div key={m.label} style={{ padding: '12px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 3, textAlign: 'center' }}>
            <div style={{ fontSize: 8, letterSpacing: 1.5, color: C.dim, marginBottom: 4 }}>{m.label}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: m.color, fontFamily: C.mono }}>{m.value}</div>
          </div>
        ))}
      </div>

      {/* Major turning point */}
      {s.majorTurningPoint && (
        <div style={{ padding: '14px', background: 'rgba(248,113,113,0.06)', border: `1px solid rgba(248,113,113,0.2)`, borderRadius: 3 }}>
          <div style={{ fontSize: 9, letterSpacing: 2, color: C.red, marginBottom: 6 }}>MAJOR TURNING POINT</div>
          <div style={{ fontSize: 11, color: C.text, lineHeight: 1.6 }}>{s.majorTurningPoint.narrative}</div>
          <div style={{ fontSize: 10, color: C.muted, marginTop: 4 }}>
            {new Date(s.majorTurningPoint.ts).toLocaleTimeString()} · {s.majorTurningPoint.severity}
          </div>
        </div>
      )}

      {/* Lessons */}
      <div style={{ padding: '14px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 3 }}>
        <div style={{ fontSize: 9, letterSpacing: 2, color: C.dim, marginBottom: 10 }}>SESSION LESSONS</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {s.lessons.map((l, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, fontSize: 11, color: C.text, lineHeight: 1.5 }}>
              <span style={{ color: C.gold, flexShrink: 0 }}>›</span>
              <span>{l}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Reasoning trace */}
      <div style={{ padding: '12px', background: 'rgba(10,22,40,0.4)', border: `1px solid ${C.border}`, borderRadius: 3 }}>
        <div style={{ fontSize: 9, letterSpacing: 2, color: C.muted, marginBottom: 8 }}>INTELLIGENCE TRACE</div>
        {s.reasoning.map((r, i) => (
          <div key={i} style={{ fontSize: 10, color: C.muted, lineHeight: 1.7, fontFamily: C.mono }}>
            {r}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Tab: Signal Evolution ──────────────────────────────────────────────────────

function SignalEvolutionTab({ history }: { history: ThesisHistoryEntry[] }) {
  if (history.length === 0) return (
    <div style={{ color: C.muted, fontSize: 11, paddingTop: 40, textAlign: 'center' }}>
      No thesis history. Run analysis to populate.
    </div>
  );

  const sorted = [...history].sort((a, b) => a.timestamp - b.timestamp);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ fontSize: 9, letterSpacing: 2, color: C.dim, marginBottom: 8 }}>
        SIGNAL EVOLUTION — {history.length} ENTRIES (oldest → newest)
      </div>
      {sorted.map((h, i) => (
        <div key={i} style={{
          display: 'flex', gap: 10, alignItems: 'center',
          padding: '7px 12px', background: C.card,
          border: `1px solid ${C.border}`, borderRadius: 3, fontSize: 11,
        }}>
          <span style={{ color: C.dim, fontSize: 9, minWidth: 55, fontFamily: C.mono }}>
            {new Date(h.timestamp).toLocaleTimeString()}
          </span>
          <BiasBadge bias={h.bias} />
          {/* Conviction bar */}
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
            <span style={{ color: C.muted, fontSize: 9, minWidth: 28 }}>Conv</span>
            <div style={{ flex: 1, height: 4, background: '#1A3050', borderRadius: 2, maxWidth: 120 }}>
              <div style={{ width: `${h.conviction}%`, height: '100%', background: C.gold, borderRadius: 2 }} />
            </div>
            <span style={{ color: C.gold, fontSize: 10, minWidth: 32, fontFamily: C.mono }}>{h.conviction}%</span>
          </div>
          {/* Actionability bar */}
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
            <span style={{ color: C.muted, fontSize: 9, minWidth: 24 }}>Act</span>
            <div style={{ flex: 1, height: 4, background: '#1A3050', borderRadius: 2, maxWidth: 100 }}>
              <div style={{
                width: `${h.actionability}%`, height: '100%', borderRadius: 2,
                background: h.actionability >= T.ACT_HIGH_THRESHOLD ? C.green : C.dim,
              }} />
            </div>
            <span style={{ color: C.dim, fontSize: 10, minWidth: 32, fontFamily: C.mono }}>{h.actionability}%</span>
          </div>
          {/* Summary */}
          <span style={{ color: '#5A7FA8', fontSize: 10, flex: 2, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {h.summary}
          </span>
        </div>
      ))}
    </div>
  );
}

// ── Tab: Structure Evolution ──────────────────────────────────────────────────

function StructureEvolutionTab({ intel, snapshots }: { intel: ReplayIntelligenceOutput; snapshots: ReplayIntelligenceSnapshot[] }) {
  const s = intel.summary;
  const evo = s?.structureEvo;

  // Gamma/wall snapshots that have structural data
  const withGamma = snapshots.filter(snap => snap.gammaRegime || snap.callWall || snap.putWall || snap.flipZone);
  const withVol   = snapshots.filter(snap => snap.volatilityRegime);

  if (snapshots.length === 0) return (
    <div style={{ color: C.muted, fontSize: 11, paddingTop: 40, textAlign: 'center' }}>
      No snapshots available. Use the Research Snapshot feature to capture structured data.
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 720 }}>
      {/* Structure evolution overview */}
      {evo && (
        <div style={{ padding: '14px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 3 }}>
          <div style={{ fontSize: 9, letterSpacing: 2, color: C.dim, marginBottom: 10 }}>STRUCTURE EVOLUTION SUMMARY</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
            {evo.callWallRange && (
              <div style={{ fontSize: 11 }}>
                <span style={{ color: C.muted }}>Call wall range: </span>
                <span style={{ color: C.text, fontFamily: C.mono }}>{evo.callWallRange[0].toFixed(2)} – {evo.callWallRange[1].toFixed(2)}</span>
              </div>
            )}
            {evo.putWallRange && (
              <div style={{ fontSize: 11 }}>
                <span style={{ color: C.muted }}>Put wall range: </span>
                <span style={{ color: C.text, fontFamily: C.mono }}>{evo.putWallRange[0].toFixed(2)} – {evo.putWallRange[1].toFixed(2)}</span>
              </div>
            )}
            <div style={{ fontSize: 11 }}>
              <span style={{ color: C.muted }}>Flip zone shifts: </span>
              <span style={{ color: C.text, fontFamily: C.mono }}>{evo.flipZoneShifts}</span>
            </div>
            <div style={{ fontSize: 11 }}>
              <span style={{ color: C.muted }}>Wall shift events: </span>
              <span style={{ color: C.text, fontFamily: C.mono }}>{evo.wallShiftEvents}</span>
            </div>
          </div>
        </div>
      )}

      {/* Gamma posture evolution */}
      {withGamma.length > 0 && (
        <div style={{ padding: '14px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 3 }}>
          <div style={{ fontSize: 9, letterSpacing: 2, color: C.dim, marginBottom: 10 }}>
            GAMMA / WALL EVOLUTION ({withGamma.length} snapshots)
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {withGamma.map((snap, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 10, padding: '4px 0', borderBottom: `1px solid ${C.border}` }}>
                <span style={{ color: C.dim, minWidth: 55, fontFamily: C.mono }}>{new Date(snap.ts).toLocaleTimeString()}</span>
                {snap.gammaRegime && <span style={{ color: '#8BACC8', flex: 2 }}>{snap.gammaRegime}</span>}
                {snap.callWall && <span style={{ color: C.muted }}>C:{snap.callWall.toFixed(0)}</span>}
                {snap.putWall  && <span style={{ color: C.muted }}>P:{snap.putWall.toFixed(0)}</span>}
                {snap.flipZone && <span style={{ color: C.gold }}>F:{snap.flipZone.toFixed(0)}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Volatility regime evolution */}
      {withVol.length > 0 && (
        <div style={{ padding: '14px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 3 }}>
          <div style={{ fontSize: 9, letterSpacing: 2, color: C.dim, marginBottom: 10 }}>VOLATILITY REGIME EVOLUTION</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {withVol.map((snap, i) => {
              const volColor = snap.volatilityRegime === 'extreme_vol' ? C.red
                : snap.volatilityRegime === 'high_vol' ? C.amber
                : snap.volatilityRegime === 'low_vol' ? C.green : C.dim;
              return (
                <div key={i} style={{ display: 'flex', gap: 10, fontSize: 10 }}>
                  <span style={{ color: C.dim, minWidth: 55, fontFamily: C.mono }}>{new Date(snap.ts).toLocaleTimeString()}</span>
                  <span style={{ color: volColor, textTransform: 'uppercase', fontWeight: 700 }}>{snap.volatilityRegime}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {withGamma.length === 0 && withVol.length === 0 && (
        <div style={{ color: C.muted, fontSize: 11, padding: '20px 0' }}>
          Gamma and volatility fields require ResearchSnapshot capture (structured data path).
          Signal Evolution and Timeline tabs show thesis-level data from the current session.
        </div>
      )}
    </div>
  );
}

// ── Outcome, Attribution, Calibration components ─────────────────────────────
// These components consume the same outcomeValidation engine as Journal.tsx.
// Replay surfaces the same intelligence in a replay-session context.

function _outcomeColor(q: string) {
  return q === 'strong_win' ? C.green : q === 'weak_win' ? '#86EFAC'
    : q === 'weak_loss' ? '#FCA5A5' : q === 'strong_loss' ? C.red
    : q === 'breakeven' ? C.dim : C.muted;
}
function _calibColor(r: string) {
  return r === 'well_calibrated' ? C.green : r === 'overconfident' ? C.red
    : r === 'underconfident' ? C.amber : C.muted;
}
function _layerColor(layer: string) {
  const m: Record<string, string> = {
    gamma: '#A78BFA', trap: C.red, structure: '#38BDF8',
    thesis: C.gold, volatility: C.amber, liquidity: '#34D399', mixed: C.dim,
  };
  return m[layer] ?? C.muted;
}

function ReplayOutcomeTab({ evaluations, summary }: { evaluations: OutcomeValidationResult[]; summary: SessionAttributionSummary }) {
  if (evaluations.length === 0) return (
    <div style={{ color: C.muted, fontSize: 11, paddingTop: 40, textAlign: 'center' }}>
      No evaluated snapshots. Capture research snapshots and mark outcomes (worked/failed) to see outcome intelligence.
    </div>
  );
  const actedOn = evaluations.filter(e => ['worked','failed','acted_on'].includes(e.decisionStatus));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 760 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
        {[
          { label: 'SNAPSHOTS',  value: `${summary.evaluationCount}`,  color: C.text },
          { label: 'WIN RATE',   value: summary.winRate !== null ? `${(summary.winRate*100).toFixed(0)}%` : '—', color: summary.winRate !== null && summary.winRate >= 0.55 ? C.green : C.red },
          { label: 'AVG CONV',   value: summary.avgConviction !== null ? `${summary.avgConviction}%` : '—', color: C.gold },
          { label: 'CALIBRATION',value: summary.overallCalibration.replace('_',' '), color: _calibColor(summary.overallCalibration) },
        ].map(m => (
          <div key={m.label} style={{ padding:'12px', background: C.card, border:`1px solid ${C.border}`, borderRadius:3, textAlign:'center' }}>
            <div style={{ fontSize:8, letterSpacing:1.5, color:C.dim, marginBottom:4 }}>{m.label}</div>
            <div style={{ fontSize:13, fontWeight:700, color:m.color, fontFamily:C.mono, textTransform:'uppercase' }}>{m.value}</div>
          </div>
        ))}
      </div>
      {actedOn.length > 0 && (
        <div style={{ padding:'14px', background: C.card, border:`1px solid ${C.border}`, borderRadius:3 }}>
          <div style={{ fontSize:9, letterSpacing:2, color:C.dim, marginBottom:10 }}>OUTCOME EVALUATION — ACTED-ON</div>
          <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
            {actedOn.map((e, i) => (
              <div key={i} style={{ display:'flex', gap:8, alignItems:'center', padding:'6px 0', borderBottom:`1px solid ${C.border}`, fontSize:10 }}>
                <span style={{ color:C.dim, minWidth:55, fontFamily:C.mono }}>{new Date(e.snapshotTs).toLocaleDateString()}</span>
                <span style={{ color: e.expectedDir==='long' ? C.green : C.red, minWidth:40, fontWeight:700 }}>{e.expectedDir.toUpperCase()}</span>
                <span style={{ color:C.dim, minWidth:40, fontFamily:C.mono }}>{e.conviction}%</span>
                <span style={{ color:_outcomeColor(e.outcomeQuality), flex:1 }}>{e.outcomeQuality.replace('_',' ')}</span>
                <span style={{ color:_layerColor(e.attribution.primaryDriver), minWidth:70 }}>{e.attribution.primaryDriver}</span>
                <span style={{ color:e.rMultiple!==null ? (e.rMultiple>0 ? C.green : C.red) : C.muted, fontFamily:C.mono, minWidth:40 }}>
                  {e.rMultiple !== null ? `${e.rMultiple}R` : '—'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      <div style={{ padding:'14px', background: C.card, border:`1px solid ${C.border}`, borderRadius:3 }}>
        <div style={{ fontSize:9, letterSpacing:2, color:C.dim, marginBottom:10 }}>LESSONS</div>
        {summary.lessons.map((l, i) => (
          <div key={i} style={{ display:'flex', gap:8, fontSize:11, color:C.text, lineHeight:1.6, marginBottom:4 }}>
            <span style={{ color:C.gold, flexShrink:0 }}>›</span><span>{l}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReplayAttributionTab({ evaluations, summary }: { evaluations: OutcomeValidationResult[]; summary: SessionAttributionSummary }) {
  const best = summary.bestEvaluation;
  if (!best) return (
    <div style={{ color: C.muted, fontSize: 11, paddingTop: 40, textAlign: 'center' }}>
      No acted-on snapshots with outcomes to attribute.
    </div>
  );
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:14, maxWidth:760 }}>
      <div style={{ padding:'14px', background:C.card, border:`1px solid ${C.border}`, borderRadius:3 }}>
        <div style={{ fontSize:9, letterSpacing:2, color:C.dim, marginBottom:8 }}>DOMINANT ATTRIBUTION LAYER</div>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <span style={{ fontSize:16, fontWeight:700, color:_layerColor(summary.dominantAttributionLayer), textTransform:'uppercase' }}>
            {summary.dominantAttributionLayer}
          </span>
        </div>
      </div>
      <div style={{ padding:'14px', background:C.card, border:`1px solid ${C.border}`, borderRadius:3 }}>
        <div style={{ fontSize:9, letterSpacing:2, color:C.dim, marginBottom:8 }}>BEST SNAPSHOT — {best.expectedDir.toUpperCase()} @ {best.conviction}%</div>
        <div style={{ fontSize:11, color:C.text, marginBottom:10 }}>{best.attribution.primaryNote}</div>
        {best.attribution.weights.map((w, i) => (
          <div key={i} style={{ display:'flex', gap:10, alignItems:'center', marginBottom:6 }}>
            <span style={{ color:_layerColor(w.layer), minWidth:80, fontSize:10, fontWeight:700, textTransform:'uppercase' }}>{w.layer}</span>
            <div style={{ flex:1, height:6, background:'#1A3050', borderRadius:3, maxWidth:200 }}>
              <div style={{ width:`${w.weight*100}%`, height:'100%', background:_layerColor(w.layer), borderRadius:3 }} />
            </div>
            <span style={{ color:C.muted, fontSize:10, fontFamily:C.mono, minWidth:35 }}>{(w.weight*100).toFixed(0)}%</span>
            <span style={{ color:'#5A7FA8', fontSize:10 }}>{w.explanation}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReplayCalibrationTab({ evaluations }: { evaluations: OutcomeValidationResult[] }) {
  const withData = evaluations.filter(e => e.calibration.calibrationResult !== 'insufficient_data');
  if (withData.length === 0) return (
    <div style={{ color: C.muted, fontSize: 11, paddingTop: 40, textAlign: 'center' }}>
      Mark snapshot outcomes as worked/failed to enable calibration review.
    </div>
  );
  const counts = { well_calibrated: 0, overconfident: 0, underconfident: 0 };
  for (const e of withData) { const r = e.calibration.calibrationResult as keyof typeof counts; if (r in counts) counts[r]++; }
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:14, maxWidth:760 }}>
      <div style={{ padding:'14px', background:C.card, border:`1px solid ${C.border}`, borderRadius:3 }}>
        <div style={{ fontSize:9, letterSpacing:2, color:C.dim, marginBottom:10 }}>CALIBRATION OVERVIEW — {withData.length} EVALUATED</div>
        <div style={{ display:'flex', gap:0, height:10, borderRadius:3, overflow:'hidden', marginBottom:10 }}>
          {counts.well_calibrated>0 && <div style={{ flex:counts.well_calibrated, background:C.green }} />}
          {counts.overconfident>0   && <div style={{ flex:counts.overconfident,   background:C.red }} />}
          {counts.underconfident>0  && <div style={{ flex:counts.underconfident,  background:C.amber }} />}
        </div>
        <div style={{ display:'flex', gap:20, fontSize:10 }}>
          <span><span style={{ color:C.green }}>{counts.well_calibrated}</span><span style={{ color:C.muted }}> well calibrated</span></span>
          <span><span style={{ color:C.red }}>{counts.overconfident}</span><span style={{ color:C.muted }}> overconfident</span></span>
          <span><span style={{ color:C.amber }}>{counts.underconfident}</span><span style={{ color:C.muted }}> underconfident</span></span>
        </div>
      </div>
      {withData.map((e, i) => (
        <div key={i} style={{ padding:'10px 12px', background:C.card, border:`1px solid ${C.border}`, borderRadius:3 }}>
          <div style={{ display:'flex', gap:10, alignItems:'center', fontSize:10, marginBottom:4 }}>
            <span style={{ color:C.dim, minWidth:55, fontFamily:C.mono }}>{new Date(e.snapshotTs).toLocaleDateString()}</span>
            <span style={{ color:C.gold, fontFamily:C.mono, minWidth:40 }}>{e.conviction}%</span>
            <span style={{ color:_calibColor(e.calibration.calibrationResult), fontWeight:700, textTransform:'uppercase', flex:1 }}>
              {e.calibration.calibrationResult.replace('_',' ')}
            </span>
            <span style={{ color:_outcomeColor(e.outcomeQuality) }}>{e.outcomeQuality.replace('_',' ')}</span>
          </div>
          <div style={{ fontSize:10, color:'#5A7FA8' }}>{e.calibration.explanation}</div>
        </div>
      ))}
    </div>
  );
}

// ── Operator Intelligence Tab (Replay) ───────────────────────────────────────

function ReplayOperatorIntelligenceTab({ intel }: { intel: SessionIntelligenceSummary }) {
  const s = intel.sessionScore;
  const gradeColors: Record<string, string> = { A: C.green, B: '#86EFAC', C: C.amber, D: C.red, F: '#F87171' };
  const qColor = (q: string) =>
    q === 'high' ? C.green : q === 'low' ? C.red : q === 'medium' ? C.amber : C.muted;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 760 }}>
      {/* Score + grade */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'stretch' }}>
        <div style={{ width: 100, padding: '12px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 3, textAlign: 'center', flexShrink: 0 }}>
          <div style={{ fontSize: 8, letterSpacing: 2, color: C.dim, marginBottom: 5 }}>GRADE</div>
          <div style={{ fontSize: 32, fontWeight: 700, fontFamily: C.mono, color: gradeColors[s.grade] ?? C.muted }}>
            {s.grade === 'insufficient_data' ? '—' : s.grade}
          </div>
          {s.grade !== 'insufficient_data' && <div style={{ fontSize: 10, color: C.muted, fontFamily: C.mono }}>{s.overall}/100</div>}
        </div>
        <div style={{ flex: 1, padding: '12px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 3 }}>
          <div style={{ fontSize: 9, letterSpacing: 2, color: C.dim, marginBottom: 8 }}>SCORE BREAKDOWN</div>
          {Object.entries(s.components).map(([k, v]) => v !== null && (
            <div key={k} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4, fontSize: 10 }}>
              <span style={{ color: C.muted, minWidth: 90 }}>{k.replace(/([A-Z])/g, ' $1').toLowerCase()}</span>
              <div style={{ flex: 1, height: 4, background: '#1A3050', borderRadius: 2, maxWidth: 140 }}>
                <div style={{ width: `${v}%`, height: '100%', borderRadius: 2,
                  background: v >= 80 ? C.green : v >= 60 ? C.amber : C.red }} />
              </div>
              <span style={{ color: C.text, fontFamily: C.mono, minWidth: 28 }}>{v}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Best / worst */}
      {(intel.strongestCall || intel.weakestCall) && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {([
            { label: 'STRONGEST CALL', item: intel.strongestCall, accent: C.green },
            { label: 'WEAKEST CALL',   item: intel.weakestCall,   accent: C.red   },
          ] as const).map(({ label, item, accent }) => item && (
            <div key={label} style={{ padding: '10px 12px', background: C.card,
              borderLeft: `3px solid ${accent}`, border: `1px solid ${accent}30`, borderRadius: 3 }}>
              <div style={{ fontSize: 8, letterSpacing: 2, color: C.dim, marginBottom: 5 }}>{label}</div>
              <div style={{ fontSize: 10, color: C.text, lineHeight: 1.5 }}>{item.explanation}</div>
              <div style={{ display: 'flex', gap: 8, marginTop: 5 }}>
                <span style={{ fontSize: 9, color: qColor(item.quality), fontWeight: 700, textTransform: 'uppercase' }}>{item.quality}</span>
                <span style={{ fontSize: 9, color: C.muted }}>timing: {item.timing}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Decision grades */}
      {intel.decisionGrades.filter(g => g.quality !== 'unknown').length > 0 && (
        <div style={{ padding: '12px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 3 }}>
          <div style={{ fontSize: 9, letterSpacing: 2, color: C.dim, marginBottom: 8 }}>DECISION GRADES</div>
          {intel.decisionGrades.filter(g => g.quality !== 'unknown').map((g, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '4px 0',
              borderBottom: `1px solid ${C.border}`, fontSize: 9 }}>
              <span style={{ color: qColor(g.quality), fontWeight: 700, minWidth: 45, textTransform: 'uppercase' }}>{g.quality}</span>
              <span style={{ color: C.muted, minWidth: 50 }}>t:{g.timing.slice(0,7)}</span>
              <span style={{ color: C.dim, minWidth: 55 }}>{g.primaryDriver}</span>
              <span style={{ color: C.text, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{g.explanation}</span>
            </div>
          ))}
        </div>
      )}

      {/* Guidance */}
      <div style={{ padding: '12px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 3 }}>
        <div style={{ fontSize: 9, letterSpacing: 2, color: C.dim, marginBottom: 8 }}>OPERATOR GUIDANCE</div>
        {intel.guidance.trustMore.length > 0 && (
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 8, color: C.green, letterSpacing: 1, marginBottom: 3, fontWeight: 700 }}>TRUST MORE</div>
            {intel.guidance.trustMore.map((t, i) => <div key={i} style={{ fontSize: 9, color: C.text, paddingLeft: 8, marginBottom: 2 }}>› {t}</div>)}
          </div>
        )}
        {intel.guidance.watchFor.length > 0 && (
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 8, color: C.amber, letterSpacing: 1, marginBottom: 3, fontWeight: 700 }}>WATCH FOR</div>
            {intel.guidance.watchFor.map((t, i) => <div key={i} style={{ fontSize: 9, color: C.text, paddingLeft: 8, marginBottom: 2 }}>› {t}</div>)}
          </div>
        )}
        {intel.guidance.calibNotes.length > 0 && (
          <div>
            <div style={{ fontSize: 8, color: C.dim, letterSpacing: 1, marginBottom: 3, fontWeight: 700 }}>CALIBRATION</div>
            {intel.guidance.calibNotes.map((t, i) => <div key={i} style={{ fontSize: 9, color: '#5A7FA8', paddingLeft: 8, marginBottom: 2 }}>› {t}</div>)}
          </div>
        )}
        {intel.guidance.trustMore.length === 0 && intel.guidance.watchFor.length === 0 && (
          <div style={{ fontSize: 9, color: C.muted }}>Mark snapshots as worked/failed in Journal to generate guidance.</div>
        )}
      </div>
    </div>
  );
}

// ── Main workspace ─────────────────────────────────────────────────────────────

type Tab = 'timeline' | 'turning' | 'summary' | 'evolution' | 'structure' | 'outcome' | 'attribution' | 'calibration' | 'operator';

const TABS: { id: Tab; label: string }[] = [
  { id: 'timeline',    label: 'Timeline'    },
  { id: 'turning',     label: 'Turning Pts' },
  { id: 'summary',     label: 'Summary'     },
  { id: 'evolution',   label: 'Signal Evo'  },
  { id: 'structure',   label: 'Structure'   },
  { id: 'outcome',     label: 'Outcome'     },
  { id: 'attribution', label: 'Attribution' },
  { id: 'calibration', label: 'Calibration' },
  { id: 'operator',    label: 'Intelligence' },
];

export function Replay() {
  const { activeSymbol } = useStore();
  const [tab, setTab]    = useState<Tab>('timeline');
  const sym    = activeSymbol ?? '';
  const thesis = sym ? getThesis(sym) : null;

  // Preferred path: ResearchSnapshot[] → richer structured data
  // Fallback path: ThesisHistoryEntry[] → thesis-only data
  const snapshots: ReplayIntelligenceSnapshot[] = useMemo(() => {
    const research = sym ? loadSnapshotsForSymbol(sym) : [];
    if (research.length > 0) return fromResearchSnapshots(sym, research);
    // Fallback: convert thesis history
    const hist = thesis?.history ?? [];
    return hist.map(h => ({
      ts: h.timestamp, symbol: sym, price: null,
      bias: h.bias, conviction: h.conviction, actionability: h.actionability,
      marketState: h.marketState, summary: h.summary ?? '',
      regimeDominant: null, regimeLabel: null, regimeConfidence: null,
      gammaRegime: null, gammaFlip: null, trapCount: 0, trapConflict: false,
      volatilityRegime: null, callWall: null, putWall: null, flipZone: null,
    }));
  }, [sym, thesis?.history]);

  // Deterministic replay intelligence — pure function of snapshots
  const intel: ReplayIntelligenceOutput = useMemo(
    () => analyseSnapshots(sym, snapshots),
    [sym, snapshots],
  );

  // Outcome validation — same engine as Journal, deterministic from snapshots + journal
  const { journal } = useStore();
  const { evaluations, summary: outcomeSummary } = useMemo(
    () => evaluateSession(sym, sym ? require('../../research/researchSnapshot').loadSnapshotsForSymbol(sym) : [], journal ?? []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sym, journal],
  );

  // Operator Intelligence — SAME function as Journal, no duplication
  const operatorIntel: SessionIntelligenceSummary = useMemo(
    () => buildSessionIntelligenceSummary(sym, evaluations, outcomeSummary, intel, null),
    [sym, evaluations, outcomeSummary, intel],
  );

  return (
    <div style={{ display: 'flex', minHeight: 0, flex: '1 1 0%', fontFamily: C.mono }}>
      <aside style={{ width: 200, flexShrink: 0, borderRight: `1px solid ${C.border}`, overflowY: 'auto' }}>
        <WatchlistSidebar />
      </aside>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Sub-tabs */}
        <div style={{ display: 'flex', borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              padding: '8px 18px', background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 10, letterSpacing: 1.5, fontFamily: 'inherit',
              color: tab === t.id ? C.gold : C.muted,
              borderBottom: tab === t.id ? `2px solid ${C.gold}` : '2px solid transparent',
            }}>
              {t.label.toUpperCase()}
            </button>
          ))}
          {/* Entry count badge */}
          {sym && (
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', paddingRight: 14, gap: 8 }}>
              <span style={{ fontSize: 9, color: C.muted }}>
                {thesis?.history?.length ?? 0} entries
              </span>
              {intel.events.length > 0 && (
                <span style={{ fontSize: 9, color: C.amber, border: `1px solid ${C.amber}40`, borderRadius: 2, padding: '1px 5px' }}>
                  {intel.events.length} events
                </span>
              )}
            </div>
          )}
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
          {sym ? (
            <>
              {tab === 'timeline'  && <TimelineTab   intel={intel} />}
              {tab === 'turning'   && <TurningPointsTab intel={intel} />}
              {tab === 'summary'   && <SummaryTab    intel={intel} />}
              {tab === 'evolution' && <SignalEvolutionTab history={thesis?.history ?? []} />}
              {tab === 'structure'   && <StructureEvolutionTab intel={intel} snapshots={snapshots} />}
              {tab === 'outcome'     && <ReplayOutcomeTab evaluations={evaluations} summary={outcomeSummary} />}
              {tab === 'attribution' && <ReplayAttributionTab evaluations={evaluations} summary={outcomeSummary} />}
              {tab === 'calibration' && <ReplayCalibrationTab evaluations={evaluations} />}
              {tab === 'operator'    && <ReplayOperatorIntelligenceTab intel={operatorIntel} />}
            </>
          ) : (
            <div style={{ color: C.muted, textAlign: 'center', paddingTop: 60, fontSize: 11, letterSpacing: 2 }}>
              SELECT A SYMBOL
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
